import React, { FC, Fragment, Suspense, useState } from 'react';
import {
  Box,
  Button,
  ButtonAppearance,
  FormControl,
  SideSheet,
  SideSheetBody,
  SideSheetClose,
  SideSheetContentCol,
  SideSheetFooter,
  SideSheetHeader,
  SideSheetSubHeader,
  Spinner,
} from '@bechtel/gds';
import {
  AddMilestoneAttachmentError,
  AddMilestoneAttachmentInput,
  AddMilestoneAttachmentPayload,
  Attachment,
  AttachmentMetadataInput,
  CommodityMilestone,
  MaterialCommodity,
  MilestoneStatusCode,
  Weld,
} from '@bechtel/types';
import { t, Trans } from '@lingui/macro';
import { FieldArray, Formik, FormikProps, FormikHelpers as FormikActions } from 'formik';
import produce from 'immer';
import { get, map } from 'lodash';
import { array, object } from 'yup';
import {
  FileUpload,
  FileUploadInput,
  FileUploadPreviewImg,
  FileUploadResponse,
  ErrorBoundary,
  SuspenseLoader,
  TitleValue,
  ViewMilestoneAttachment,
} from '@bechtel/client-common';
import { useWorkPackagePermissions } from '@bechtel/client-work-packages';
import { ApolloError, useMutation } from '@apollo/client';
import { ADD_MILESTONE_ATTACHMENT, DELETE_BLOCK_MEDIA } from '@bechtel/client-gql';
import { AttachmentHeadingStyled } from './styled';
import { AddAttachmentError } from './error';

export interface AddViewAttachmentProps {
  closeModal: () => void;
  milestone: CommodityMilestone;
}

interface FormikState {
  mediaMetadata?: AddMilestoneAttachmentInput['attachmentMetadata'];
}

const initialValues: FormikState = {
  mediaMetadata: [],
};

const addMilestoneAttachmentSchema = object().shape({
  mediaMetadata: array()
    .required()
    .max(3, t`Max 3 photos can be uploaded`),
});

export const AddViewAttachment: FC<AddViewAttachmentProps> = ({ milestone, closeModal }) => {
  const [attachments, setAttachments] = useState<Attachment[]>(milestone.attachments);
  const [addAttachmentError, setAddAttachmentError] = useState<AddMilestoneAttachmentError | ApolloError | null>();
  const [deleteBlockMedia, { loading: deleteLoading, data: deleteData }] = useMutation(DELETE_BLOCK_MEDIA);
  const deleteError = get(deleteData, 'deleteBlockMedia.error', null);
  const [addMilestoneAttachment, { loading: mutationLoading }] = useMutation<
    {
      addMilestoneAttachmentPayload: AddMilestoneAttachmentPayload;
    },
    {
      input: AddMilestoneAttachmentInput;
    }
  >(ADD_MILESTONE_ATTACHMENT, {
    notifyOnNetworkStatusChange: true,
    onCompleted: (data) => {
      const addMilestoneAttachmentError = get(data, 'addMilestoneAttachment.error');
      if (addMilestoneAttachmentError) {
        setAddAttachmentError(addMilestoneAttachmentError);
      } else {
        const addedMilestoneAttachments = get(data, 'addMilestoneAttachment.milestone.attachments');
        setAttachments(addedMilestoneAttachments);
        if (addAttachmentError) setAddAttachmentError(null);
      }
    },
    onError: (error) => {
      setAddAttachmentError(error);
    },
  });
  const milestoneStatus = milestone.currentStatus.codeI18n;
  const showUpload =
    milestoneStatus === MilestoneStatusCode.CLAIMED ||
    milestoneStatus === MilestoneStatusCode.APPROVED ||
    milestoneStatus === MilestoneStatusCode.REJECTED;
  //@ts-ignore:ignore undefined tupe warning as commodity wrapper will either have a weld or material commodity always
  const commodity: MaterialCommodity | Weld = milestone?.commodity.materialCommodity
    ? milestone?.commodity.materialCommodity
    : milestone?.commodity.weld;

  const assignedCrewMembers = [
    ...(commodity.workPackage?.primaryOwners ?? []),
    ...(commodity.workPackage?.secondaryOwners ?? []),
    ...(commodity.workPackage?.otherOwners ?? []),
  ];

  const { canDeleteMilestoneAttachment } = useWorkPackagePermissions(assignedCrewMembers);
  return (
    <Fragment>
      <SideSheet visible closable onDismiss={closeModal}>
        <SideSheetHeader data-testid="attachment-header" title={t`Attachments`}>
          <SideSheetClose onClick={closeModal}></SideSheetClose>
        </SideSheetHeader>
        <SideSheetSubHeader>
          <Box mb={{ sm: '300' }}>
            <TitleValue title={t`Work Package`} value={commodity.workPackage.tag}></TitleValue>
          </Box>
          <Box mr={{ sm: '300' }} mb={{ sm: '300' }}>
            <TitleValue title={commodity.type.name} value={commodity.tag}></TitleValue>
          </Box>
          <Box mr={{ sm: '300' }}>
            <TitleValue title={t`Milestone`} value={milestone.name}></TitleValue>
          </Box>
        </SideSheetSubHeader>
        <SideSheetBody>
          <SideSheetContentCol>
            {showUpload && (
              <Formik
                initialValues={initialValues}
                validationSchema={addMilestoneAttachmentSchema}
                onSubmit={async (values: FormikState, actions: FormikActions<FormikState>) => {
                  await addMilestoneAttachment({
                    variables: {
                      input: {
                        commodityMilestoneId: milestone.id,
                        attachmentMetadata: values.mediaMetadata,
                      },
                    },
                  });
                  actions.resetForm({ values: initialValues });
                  actions.setSubmitting(false);
                }}
              >
                {({ isValid, dirty, handleSubmit, values }: FormikProps<FormikState>) => {
                  return (
                    <Fragment>
                      <Box mb={{ sm: '600' }}>
                        <AttachmentHeadingStyled>
                          <Trans>
                            Add New Attachment ({values?.mediaMetadata?.length ? values?.mediaMetadata?.length : 0}/3)
                          </Trans>
                        </AttachmentHeadingStyled>
                      </Box>
                      <Box flexDirection={{ sm: 'column' }} mb={{ sm: '500' }} px={{ sm: '500' }}>
                        <FieldArray name="mediaMetadata">
                          {({ form, push, remove }) => {
                            return (
                              <FormControl label={<Trans>Upload a Photo</Trans>} css="flex-grow: 1;">
                                {!deleteError ? (
                                  <FileUpload
                                    filesCount={form.values.mediaMetadata.length ? form.values.mediaMetadata.length : 0}
                                    maxFiles={3}
                                    maxFileSizeMB={10}
                                    acceptFileTypes="image/jpeg, image/png"
                                    onSuccess={(res: FileUploadResponse, file) => {
                                      const newMediaData: AttachmentMetadataInput = {
                                        fileName: file.name,
                                        fileSize: file.size,
                                        attachmentId: res.attachmentId,
                                        mimeType: file.type,
                                      };
                                      push(newMediaData);
                                    }}
                                  >
                                    {({ getInputProps, getRootProps, loading, files, setFiles, acceptFileTypes }) => {
                                      return (
                                        <Fragment>
                                          <FileUploadInput
                                            getInputProps={getInputProps}
                                            getRootProps={getRootProps}
                                            loading={loading || deleteLoading}
                                            acceptFileTypes={acceptFileTypes}
                                          />
                                          {deleteError && (
                                            <Box my={{ sm: '200' }}>
                                              <Trans>There was an error when deleting a file.</Trans>
                                            </Box>
                                          )}
                                          {map(form.values.mediaMetadata, (file, index: number) => {
                                            return (
                                              <Box
                                                mb={{ sm: '500' }}
                                                key={`${file.name}.${index}`}
                                                flexDirection={{
                                                  sm: 'column',
                                                }}
                                              >
                                                <FileUploadPreviewImg
                                                  file={files[index]}
                                                  onDelete={async () => {
                                                    const { data } = await deleteBlockMedia({
                                                      variables: {
                                                        input: {
                                                          attachmentId: files[index].attachmentId,
                                                        },
                                                      },
                                                    });

                                                    if (!data.deleteBlockAttachment.error) {
                                                      remove(index);
                                                      setFiles(
                                                        produce((draft) => {
                                                          // @ts-ignore declaration throwing unknownn type error for draft
                                                          draft.splice(index, 1);
                                                        })
                                                      );
                                                    }
                                                  }}
                                                />
                                              </Box>
                                            );
                                          })}
                                        </Fragment>
                                      );
                                    }}
                                  </FileUpload>
                                ) : (
                                  <div>
                                    <Trans>There was an error when deleting the file</Trans>
                                  </div>
                                )}
                              </FormControl>
                            );
                          }}
                        </FieldArray>
                        <Button
                          appearance={ButtonAppearance.PRIMARY}
                          disabled={!isValid || !dirty || mutationLoading}
                          data-testid="submit-button"
                          onClick={() => {
                            handleSubmit();
                          }}
                        >
                          {mutationLoading && <Spinner />}
                          {!mutationLoading && t`Submit`}
                        </Button>
                      </Box>
                      <AttachmentHeadingStyled>
                        <Trans>Attachments</Trans>
                      </AttachmentHeadingStyled>
                    </Fragment>
                  );
                }}
              </Formik>
            )}
            <ViewMilestoneAttachment
              attachments={attachments}
              setAttachments={setAttachments}
              commodityMilestoneId={milestone.id}
              canDeleteMilestoneAttachment={canDeleteMilestoneAttachment}
            />
          </SideSheetContentCol>
        </SideSheetBody>
        <SideSheetFooter>
          <Box justifyContent={{ sm: 'flex-end' }}>
            <Box>
              <Button appearance={ButtonAppearance.SECONDARY} onClick={closeModal} data-testid="close-button">
                <Trans>Close</Trans>
              </Button>
            </Box>
          </Box>
        </SideSheetFooter>
      </SideSheet>
      <ErrorBoundary>
        <Suspense fallback={<SuspenseLoader></SuspenseLoader>}>
          {addAttachmentError && (
            <AddAttachmentError
              error={addAttachmentError}
              close={() => {
                setAddAttachmentError(null);
              }}
            />
          )}
        </Suspense>
      </ErrorBoundary>
    </Fragment>
  );
};
